import React, { useState, useCallback } from 'react';
import { Modal, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useDropzone, FileWithPath } from 'react-dropzone';
import { enqueueSnackbar } from 'notistack';
import { ExportHubAPI, ImportShipmentsResult } from 'api/ExportHubAPI';

interface Props {
  open: boolean;
  groupId: string | undefined;
  onClose: () => void;
  // Called after a successful import (≥1 row) so the parent can refresh its
  // ledger. The modal stays open to show the result summary.
  onImported: () => void;
}

type Phase = 'select' | 'uploading' | 'result';

// Friendly headers the server parser understands (SHIPMENT_ALIASES), plus two
// example rows — a shipment and the return that credits part of it back.
const TEMPLATE_HEADERS = [
  'License Number',
  'Internal SKU',
  'Type',
  'Date',
  'Reference',
  'Quantity',
  'Unit of Measure',
  'Unit Value',
  'Total Value',
  'ECCN',
  'Destination',
  'Consignee',
];

const TEMPLATE_SAMPLES = [
  [
    '057-2024-118432',
    'SKU-1001',
    'Shipment',
    '2026-07-29',
    'X20260729114',
    '20',
    'EA',
    '38900',
    '',
    '9A610.a',
    'United Kingdom',
    'Brightwater Defence Ltd',
  ],
  [
    '057-2024-118432',
    'SKU-1001',
    'Return',
    '2026-08-04',
    'RMA-2201',
    '2',
    'EA',
    '36000',
    '',
    '9A610.a',
    'United Kingdom',
    'Brightwater Defence Ltd',
  ],
];

const csvCell = (v: string): string =>
  /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;

const downloadTemplate = (): void => {
  const lines = [
    TEMPLATE_HEADERS.map(csvCell).join(','),
    ...TEMPLATE_SAMPLES.map((r) => r.map(csvCell).join(',')),
  ];
  const blob = new Blob([`${lines.join('\n')}\n`], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'export-hub-shipments-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Bulk shipment import — how a compliance team actually reconciles, a quarter
 * of AES filings at a time rather than one modal per shipment.
 *
 * Rows name their license by number and their product by SKU, because that is
 * what an invoice register contains. Leave Total Value blank to let quantity ×
 * unit value stand; fill it in when the invoice isn't a clean multiple.
 */
const ImportShipmentsModal: React.FC<Props> = ({
  open,
  groupId,
  onClose,
  onImported,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<Phase>('select');
  const [result, setResult] = useState<ImportShipmentsResult | null>(null);

  const reset = useCallback((): void => {
    setFile(null);
    setPhase('select');
    setResult(null);
  }, []);

  const handleClose = useCallback((): void => {
    if (phase === 'uploading') return; // don't close mid-upload
    reset();
    onClose();
  }, [phase, reset, onClose]);

  const onDrop = useCallback((accepted: FileWithPath[]): void => {
    const f = accepted[0] as File | undefined;
    if (f) setFile(f);
  }, []);

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop: onDrop,
      accept: [
        'text/csv',
        '.csv',
        '.xlsx',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ],
      multiple: false,
    });

  const handleSubmit = async (): Promise<void> => {
    if (!file || !groupId) {
      enqueueSnackbar('Select a file to import.', { variant: 'warning' });
      return;
    }
    setPhase('uploading');
    try {
      const res = await ExportHubAPI.importShipments(groupId, file);
      setResult(res);
      setPhase('result');
      if (res.imported > 0) {
        onImported();
        enqueueSnackbar(
          `Imported ${res.imported} transaction${
            res.imported === 1 ? '' : 's'
          } across ${res.licenses_touched} license${
            res.licenses_touched === 1 ? '' : 's'
          }.`,
          { variant: 'success' },
        );
      } else {
        enqueueSnackbar('Nothing was imported — check the row errors.', {
          variant: 'warning',
        });
      }
    } catch (e: any) {
      setPhase('select');
      enqueueSnackbar(
        e?.response?.data?.message ||
          (e instanceof Error ? e.message : 'Import failed.'),
        { variant: 'error' },
      );
    }
  };

  return (
    <Modal open={open} onClose={handleClose} className='eh-modal-root'>
      <div className='eh-modal' role='dialog' aria-label='Import shipments'>
        <div className='eh-modal-head'>
          <h2 className='eh-modal-title'>Import Shipments</h2>
          <button
            className='eh-modal-close'
            onClick={handleClose}
            aria-label='Close'
            type='button'
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className='eh-modal-body'>
          {phase === 'select' && (
            <>
              <p className='eh-modal-subtitle'>
                Upload a CSV or XLSX of transactions. Each row draws down the
                license it names by number, against the product it names by SKU.
                Leave <strong>Total Value</strong> blank to use quantity × unit
                value; fill it in to record what the invoice actually said.
              </p>

              <button
                className='eh-linkish'
                type='button'
                onClick={downloadTemplate}
                style={{ marginBottom: 12 }}
              >
                <DescriptionOutlinedIcon style={{ fontSize: 16 }} /> Download CSV
                template
              </button>

              <div
                {...getRootProps()}
                className={`eh-dropzone ${isDragActive ? 'active' : ''} ${
                  isDragReject ? 'reject' : ''
                } ${file ? 'has-file' : ''}`}
              >
                <input {...getInputProps()} />
                {file ? (
                  <div className='eh-dropzone-inner'>
                    <CheckCircleOutlineIcon
                      style={{ fontSize: 22, color: 'var(--full-bar-color)' }}
                    />
                    <span className='eh-dropzone-file'>{file.name}</span>
                    <span className='eh-dropzone-sub'>Click to replace</span>
                  </div>
                ) : (
                  <div className='eh-dropzone-inner'>
                    <span className='eh-dropzone-text'>
                      <strong>Click to upload</strong> or drag &amp; drop
                    </span>
                    <span className='eh-dropzone-sub'>CSV or XLSX</span>
                    {isDragReject && (
                      <span className='eh-dropzone-err'>
                        Unsupported file type.
                      </span>
                    )}
                  </div>
                )}
              </div>

              <p className='eh-field-hint'>
                Rows import as posted transactions and move each license&apos;s
                consumed value immediately. A row naming a license or SKU that
                doesn&apos;t exist is reported, not guessed at.
              </p>
            </>
          )}

          {phase === 'uploading' && (
            <div className='eh-import-busy'>
              <CircularProgress size={32} />
              <span className='eh-import-busy-text'>
                Importing transactions and recalculating licenses…
              </span>
            </div>
          )}

          {phase === 'result' && result && (
            <div className='eh-import-result'>
              <div className='eh-import-summary'>
                {result.failed === 0 ? (
                  <CheckCircleOutlineIcon
                    style={{ fontSize: 28, color: 'var(--full-bar-color)' }}
                  />
                ) : (
                  <ErrorOutlineIcon
                    style={{ fontSize: 28, color: 'var(--mid-color)' }}
                  />
                )}
                <div>
                  <div className='eh-import-summary-title'>
                    Imported {result.imported} of {result.rows_total} row
                    {result.rows_total === 1 ? '' : 's'}
                  </div>
                  <div className='eh-import-summary-sub'>
                    {result.licenses_touched} license
                    {result.licenses_touched === 1 ? '' : 's'} updated
                    {result.failed > 0
                      ? ` · ${result.failed} row${
                          result.failed === 1 ? '' : 's'
                        } skipped`
                      : ''}
                  </div>
                </div>
              </div>

              {result.errors.length > 0 && (
                <div className='eh-import-errors'>
                  <div className='eh-import-errors-head'>
                    Row errors{' '}
                    {result.errors.length < result.failed
                      ? `(showing first ${result.errors.length})`
                      : ''}
                  </div>
                  <div className='eh-import-errors-list'>
                    {result.errors.map((err) => (
                      <div
                        className='eh-import-error-row'
                        key={`${err.row}-${err.issues[0]}`}
                      >
                        <span className='eh-import-error-rownum'>
                          Row {err.row}
                        </span>
                        <span className='eh-import-error-issues'>
                          {err.issues.join('; ')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className='eh-modal-foot'>
          {phase === 'result' ? (
            <>
              <button className='btn-outline' type='button' onClick={reset}>
                Import another
              </button>
              <button
                className='btn-primary'
                type='button'
                onClick={handleClose}
              >
                Done
              </button>
            </>
          ) : (
            <>
              <button
                className='btn-outline'
                type='button'
                onClick={handleClose}
                disabled={phase === 'uploading'}
              >
                Cancel
              </button>
              <button
                className='btn-primary'
                type='button'
                onClick={handleSubmit}
                disabled={!file || phase === 'uploading'}
                style={{ opacity: !file || phase === 'uploading' ? 0.6 : 1 }}
              >
                {phase === 'uploading' ? 'Importing…' : 'Import'}
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ImportShipmentsModal;
